import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import { ArrowLeft } from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/customers/${id}`).then((r) => setData(r.data)); }, [id]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const { customer, sales, outstanding } = data;

  return (
    <div data-testid="customer-detail-page">
      <Link to="/customers" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={14} /> Back to Customers
      </Link>
      <PageHeader overline="Customer" title={customer.name} description={`${customer.phone || "—"} · ${customer.email || "—"}`} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total Purchases</div>
          <div className="font-display text-2xl font-bold mt-1">{sales.length}</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Outstanding Balance</div>
          <div className={`font-display text-2xl font-bold mt-1 ${outstanding > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtMoney(outstanding)}</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Driver License</div>
          <div className="font-display text-2xl font-bold mt-1 font-mono">{customer.driver_license || "—"}</div>
        </Card>
      </div>

      <Card className="p-5 mb-6">
        <h3 className="font-display text-lg font-semibold mb-3">Customer Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-6 text-sm">
          {[["Address", customer.address], ["Source", customer.source], ["Created", fmtDate(customer.created_at)]].map(([l, v]) => (
            <div key={l}>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div>
              <div className="font-medium">{v || "—"}</div>
            </div>
          ))}
        </div>
        {customer.notes && <div className="mt-4 pt-4 border-t border-border text-sm"><div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Notes</div>{customer.notes}</div>}
      </Card>

      <h3 className="font-display text-xl font-semibold mb-3">Purchase History</h3>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Invoice", "Date", "Vehicle", "Sale Price", "Total", "Balance"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {sales.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No purchases yet.</td></tr>}
            {sales.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-secondary/40">
                <td className="px-4 py-3 font-mono text-xs"><Link to={`/sales/${s.id}`} className="hover:text-blue-600">{s.invoice_number}</Link></td>
                <td className="px-4 py-3">{fmtDate(s.sale_date || s.created_at)}</td>
                <td className="px-4 py-3">{s.vehicle ? `${s.vehicle.year} ${s.vehicle.make} ${s.vehicle.model}` : "—"}</td>
                <td className="px-4 py-3">{fmtMoney(s.sale_price)}</td>
                <td className="px-4 py-3 font-semibold">{fmtMoney(s.total)}</td>
                <td className={`px-4 py-3 font-semibold ${s.balance > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtMoney(s.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
